import React, { useEffect, useMemo, useState } from "react";
import Moment from "react-moment";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../../app/store/auth";
import { useDispatch, useSelector } from "react-redux";
import { getFieldData } from "../../Services/appService";
import { getBackgroundChecks, loadBackgroundChecks } from "../../app/store/candidate";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import { getComponentUserStatus, UpdateComponentsUserStatus } from "../../app/store/tools";

const BACKGROUND_CHECK_STYLES = {
  card: {
    borderRadius: "22px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.05)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "20px 22px 16px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  },
  titleRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "15px",
    border: "1px solid rgba(191, 219, 254, 1)",
  },
  title: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "6px",
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#64748b",
    margin: 0,
  },
  cardBody: {
    padding: "18px 22px 22px 22px",
  },
  utilityRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "14px",
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#64748b",
    margin: 0,
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  statCard: {
    borderRadius: "18px",
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 10px 26px rgba(37, 99, 235, 0.06)",
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "6px",
  },
  statValue: {
    fontSize: "26px",
    fontWeight: 800,
    lineHeight: 1,
    color: "#0f172a",
  },
  statHint: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#64748b",
    marginTop: "6px",
  },
  tableWrap: {
    border: "1px solid rgba(226, 232, 240, 1)",
    borderRadius: "18px",
    overflow: "auto",
    backgroundColor: "#ffffff",
  },
  table: {
    marginBottom: 0,
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "14px 16px",
  },
  cell: {
    verticalAlign: "middle",
    padding: "22px 16px",
    borderTop: "1px solid rgba(226, 232, 240, 0.75)",
  },
  serialBadge: {
    minWidth: "34px",
    height: "34px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4338ca",
    fontSize: "13px",
    fontWeight: 800,
  },
  typeText: {
    fontSize: "15px",
    fontWeight: 700,
    lineHeight: 1.35,
    color: "#0f172a",
  },
  datePrimary: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "4px",
  },
  dateSecondary: {
    fontSize: "12px",
    color: "#64748b",
  },
  commentText: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: "#475569",
    marginBottom: "6px",
    whiteSpace: "pre-line",
  },
  metaText: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#64748b",
  },
  emptyState: {
    padding: "30px 22px",
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: "16px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "6px",
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: "#64748b",
    margin: 0,
  },
};

const getStatusConfig = (status) => {
  if (status === true) {
    return {
      label: "Passed",
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "7px 12px",
        borderRadius: "999px",
        background: "rgba(34, 197, 94, 0.12)",
        color: "#15803d",
        fontSize: "12px",
        fontWeight: 800,
        border: "1px solid rgba(134, 239, 172, 0.6)",
      },
      icon: "fa-check-circle",
    };
  }

  if (status === false) {
    return {
      label: "Failed",
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "7px 12px",
        borderRadius: "999px",
        background: "rgba(239, 68, 68, 0.12)",
        color: "#b91c1c",
        fontSize: "12px",
        fontWeight: 800,
        border: "1px solid rgba(252, 165, 165, 0.7)",
      },
      icon: "fa-times-circle",
    };
  }

  return {
    label: "In Process",
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "7px 12px",
      borderRadius: "999px",
      background: "rgba(245, 158, 11, 0.12)",
      color: "#b45309",
      fontSize: "12px",
      fontWeight: 800,
      border: "1px solid rgba(253, 186, 116, 0.7)",
    },
    icon: "fa-clock-o",
  };
};

export default function BackgroundChecks() {
  const route = [
    { name: "Task Management", url: "" },
    { name: "Onboarding Steps", url: "" },
  ];

  const [bcChecks, setBcChecks] = useState([]);
  const [entityType, setEntityType] = useState("");

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const backgroundCheckSelector = useSelector(getBackgroundChecks);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const count = entityType !== "C2C Basis" ? "4" : "3";

  useEffect(() => {
    dispatch(setCurrentPageTitle(`STEP-${count} : Background Check`));
    dispatch(setCurrentRoute(route));
  }, []);

  useEffect(() => {
    if (!userDetailsSelector) {
      dispatch(loadUserDetails(currentUser.id));
    }

    dispatch(loadBackgroundChecks(currentUser.id));
  }, []);

  useEffect(() => {
    if (userDetailsSelector) {
      const type = getFieldData(
        userDetailsSelector.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
      setEntityType(type);
    }
  }, [userDetailsSelector]);

  useEffect(() => {
    if (backgroundCheckSelector.length > 0) {
      const filterBc = backgroundCheckSelector.filter((x) => x.sendStatus === true);

      if (filterBc.length > 0) {
        setBcChecks(filterBc);
        let bcItems = "";
        filterBc.forEach((x, key) => {
          if (key > 0) bcItems += ",";
          bcItems += x.id;
        });
        handleUpdateComponent(bcItems, filterBc[filterBc.length - 1].ciid);
      } else {
        setBcChecks([]);
      }
    } else {
      setBcChecks([]);
    }
  }, [backgroundCheckSelector]);

  const handleUpdateComponent = (compItems, ciid) => {
    const comp = componentUserStatusSelector.filter((x) => x.compId === 26);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Background Checks",
      performerId: currentUser.id,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 26,
    };
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  };

  const summary = useMemo(() => {
    const total = bcChecks.length;
    const passed = bcChecks.filter((item) => item.status === true).length;
    const failed = bcChecks.filter((item) => item.status === false).length;
    const inProcess = total - passed - failed;

    return { total, passed, failed, inProcess };
  }, [bcChecks]);

  return (
    <React.Fragment>
      <div className="col-md-12 mt-2">
        <div className="card border-1 shadow-none" style={BACKGROUND_CHECK_STYLES.card}>
          <div className="card-header border-1" style={BACKGROUND_CHECK_STYLES.cardHeader}>
            <div style={BACKGROUND_CHECK_STYLES.titleRow}>
              <span style={BACKGROUND_CHECK_STYLES.titleIcon}>
                <i className="fa fa-search" aria-hidden="true"></i>
              </span>
              <div style={BACKGROUND_CHECK_STYLES.title}>Background Check</div>
            </div>
            <p style={BACKGROUND_CHECK_STYLES.subtitle}>
              Track the checks that have been issued for your onboarding. Each row shows the verification type,
              current result, and the latest reviewer notes.
            </p>
          </div>
          <div className="card-body border-1" style={BACKGROUND_CHECK_STYLES.cardBody}>
            <div style={BACKGROUND_CHECK_STYLES.utilityRow}>
              <p style={BACKGROUND_CHECK_STYLES.helperText}>
                Review the latest screening outcomes here. Status updates and reviewer comments will appear as soon as they are received.
              </p>
            </div>
            <div style={BACKGROUND_CHECK_STYLES.statsRow}>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statLabel}>Checks Sent</div>
                <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.total}</div>
                <div style={BACKGROUND_CHECK_STYLES.statHint}>Active background check items currently visible.</div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statLabel}>Passed</div>
                <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.passed}</div>
                <div style={BACKGROUND_CHECK_STYLES.statHint}>Checks that cleared successfully.</div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statLabel}>Failed</div>
                <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.failed}</div>
                <div style={BACKGROUND_CHECK_STYLES.statHint}>Items that need review or follow-up.</div>
              </div>
              <div style={BACKGROUND_CHECK_STYLES.statCard}>
                <div style={BACKGROUND_CHECK_STYLES.statLabel}>In Review</div>
                <div style={BACKGROUND_CHECK_STYLES.statValue}>{summary.inProcess}</div>
                <div style={BACKGROUND_CHECK_STYLES.statHint}>Items still pending a final outcome.</div>
              </div>
            </div>
            <div className="table-responsive" style={BACKGROUND_CHECK_STYLES.tableWrap}>
              {bcChecks.length > 0 ? (
                <table className="table align-items-center table-hover mb-0" style={BACKGROUND_CHECK_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}></th>
                      <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Date & Time</th>
                      <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Type</th>
                      <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Status</th>
                      <th style={BACKGROUND_CHECK_STYLES.tableHeadCell}>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bcChecks.map((item, key) => {
                      const statusConfig = getStatusConfig(item.status);
                      return (
                        <tr key={item.id || key}>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <span style={BACKGROUND_CHECK_STYLES.serialBadge}>{key + 1}</span>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={BACKGROUND_CHECK_STYLES.datePrimary}>
                              {item.updatedDateTime ? <Moment format="MMM Do YYYY">{item.updatedDateTime}</Moment> : "--"}
                            </div>
                            <div style={BACKGROUND_CHECK_STYLES.dateSecondary}>
                              {item.updatedDateTime ? <Moment format="h:mm a">{item.updatedDateTime}</Moment> : "--"}
                            </div>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={BACKGROUND_CHECK_STYLES.typeText}>{item.name}</div>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <span style={statusConfig.style}>
                              <i className={`fa ${statusConfig.icon}`} aria-hidden="true"></i>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td style={BACKGROUND_CHECK_STYLES.cell}>
                            <div style={BACKGROUND_CHECK_STYLES.commentText}>
                              {item.instructionNotes || "No reviewer comments available yet."}
                            </div>
                            {item.updatedDateTime && (
                              <div style={BACKGROUND_CHECK_STYLES.metaText}>
                                Updated: <Moment format="MMM Do YYYY, h:mm a">{item.updatedDateTime}</Moment>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={BACKGROUND_CHECK_STYLES.emptyState}>
                  <div style={BACKGROUND_CHECK_STYLES.emptyTitle}>No background checks available yet</div>
                  <p style={BACKGROUND_CHECK_STYLES.emptyText}>
                    Once a screening item is sent, its current status and notes will appear here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
